import { useEffect, useMemo, useRef, useState } from 'react';

import { useAnchoredDropdown } from '../common/useAnchoredDropdown';
import { buildSearchDropdownResults } from './searchDropdownResults';

import type { SearchDropdownResult } from './searchDropdownResults';
import type {
  DashboardStory,
  Folder,
  FolderStructure,
  OtherVideo,
} from '@src/types.ts/story';
import type { KeyboardEvent } from 'react';

const MAX_DROPDOWN_HEIGHT = 400;

type Params = {
  /** Trimmed, DEBOUNCED query — the dropdown opens only once results match what was typed. */
  query: string;
  structure: FolderStructure;
  /** The page-filtered (and visibility-filtered) story list. */
  stories: DashboardStory[];
  onSelectFolder: (folder: Folder) => void;
  onSelectStory: (story: DashboardStory) => void;
  onSelectClip: (clip: OtherVideo, parentStory: DashboardStory) => void;
};

/**
 * State owner for the Story Manager search results dropdown: derives the
 * grouped results, anchors the portal below the search input (via
 * `useAnchoredDropdown`, which also owns outside-click / Escape / scroll /
 * resize dismissal), and drives keyboard navigation over the flat result list.
 */
export function useSearchResultsDropdown({
  query,
  structure,
  stories,
  onSelectFolder,
  onSelectStory,
  onSelectClip,
}: Params) {
  // "Dismissed" survives until the user types again, so Escape / outside
  // click / selecting a result keep the dropdown closed while the query text
  // is still sitting in the input.
  const [dismissed, setDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const triggerRef = useRef<HTMLDivElement>(null);

  const results = useMemo(
    () => buildSearchDropdownResults(structure, stories, query),
    [structure, stories, query],
  );
  const flatLength = results.flat.length;

  const isOpen = query.length > 0 && !dismissed;
  const { menuRef, style } = useAnchoredDropdown(
    isOpen,
    triggerRef,
    () => setDismissed(true),
    MAX_DROPDOWN_HEIGHT,
  );

  // A new query or result set invalidates the keyboard position.
  useEffect(() => {
    setActiveIndex(-1);
  }, [query, flatLength]);

  /** Call on every keystroke so typing re-opens a dismissed dropdown. */
  const notifyTyping = () => setDismissed(false);

  const selectResult = (result: SearchDropdownResult) => {
    switch (result.type) {
      case 'folder':
        onSelectFolder(result.folder);
        break;
      case 'story':
        onSelectStory(result.story);
        break;
      case 'clip':
        onSelectClip(result.clip, result.story);
        break;
    }
    setDismissed(true);
  };

  // Escape is handled by useAnchoredDropdown's document-level listener; every
  // key other than the ones below falls through so plain typing is unaffected.
  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || flatLength === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => (prev + 1) % flatLength);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev =>
          prev < 0 ? flatLength - 1 : (prev - 1 + flatLength) % flatLength,
        );
        break;
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < flatLength) {
          e.preventDefault();
          selectResult(results.flat[activeIndex]);
        }
        break;
      default:
        break;
    }
  };

  return {
    isOpen,
    triggerRef,
    menuRef,
    style,
    results,
    activeIndex,
    notifyTyping,
    selectResult,
    handleInputKeyDown,
  };
}
